var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
import { round, sumBy, times } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useFormlessContext } from '../../hooks';
import { LocationTableEntryMode } from '../constants';
import { useGroupedLocations, useLocationIndexMap, useQueueLocations } from '../selectors';
import { computeChunkSummary, computeTypeSums, convertVolumeField } from './utils';
export var useEntryModeConversion = function (_a) {
  var _b, _c, _d;
  var name = _a.name,
    entryModeField = _a.entryModeField,
    decimal = _a.decimal;
  var control = ((_b = useFormlessContext()) !== null && _b !== void 0 ? _b : {}).control;
  var _e = (_c = useFormContext()) !== null && _c !== void 0 ? _c : {},
    setValue = _e.setValue,
    clearErrors = _e.clearErrors;
  var _f = useState(),
    pendingEntryMode = _f[0],
    setPendingEntryMode = _f[1];
  var queueLocationsRaw = useWatch({
    name: 'queueLocations',
    control: control
  });
  var queueLocationsData = useMemo(function () {
    return queueLocationsRaw !== null && queueLocationsRaw !== void 0 ? queueLocationsRaw : [];
  }, [queueLocationsRaw]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  var totalVolumesData = (_d = useWatch({
    name: 'volumes',
    control: control
  })) !== null && _d !== void 0 ? _d : [];
  var entryMode = useWatch({
    name: entryModeField !== null && entryModeField !== void 0 ? entryModeField : '',
    control: control
  });
  var shouldConvert = ['volume', 'productionHour'].includes(name);
  var hasLocations = !!queueLocationsData.length;
  var shouldConfirmAttritionModeChange = name === 'attrition' && entryModeField === 'attritionEntryMode';
  var hasEnteredAttritionData = useMemo(function () {
    if (!shouldConfirmAttritionModeChange) return false;
    var hasAttritionValue = function (value) {
      return value !== null && value !== undefined && value !== '';
    };
    if (hasLocations) {
      return queueLocationsData.some(function (location) {
        var _a;
        return (_a = location.volumes) === null || _a === void 0 ? void 0 : _a.some(function (volume) {
          return hasAttritionValue(volume.attrition);
        });
      });
    }
    return totalVolumesData.some(function (volume) {
      return hasAttritionValue(volume.attrition);
    });
  }, [hasLocations, queueLocationsData, shouldConfirmAttritionModeChange, totalVolumesData]);
  var applyEntryModeChange = useCallback(function (newMode) {
    if (!entryModeField) return;
    var oldMode = entryMode !== null && entryMode !== void 0 ? entryMode : LocationTableEntryMode.Percentage;
    if (newMode === oldMode) return;
    /** Shared per-period conversion applied to both data paths */
    var convertField = function (rawVal, periodIdx) {
      return convertVolumeField({
        rawVal: rawVal,
        periodIdx: periodIdx,
        totalVolumesData: totalVolumesData,
        oldMode: oldMode,
        newMode: newMode,
        decimal: decimal,
        shouldConvert: shouldConvert,
        targetField: name
      });
    };
    if (hasLocations) {
      var converted_1 = queueLocationsData.map(function (locItem) {
        var _a, _b;
        return __assign(__assign({}, locItem), {
          volumes: (_b = (_a = locItem.volumes) === null || _a === void 0 ? void 0 : _a.map(function (v, periodIdx) {
            var _a;
            return __assign(__assign({}, v), (_a = {}, _a[name] = convertField(v[name], periodIdx), _a));
          })) !== null && _b !== void 0 ? _b : []
        });
      });
      queueMicrotask(function () {
        setValue === null || setValue === void 0 ? void 0 : setValue('queueLocations', converted_1, {
          shouldDirty: true
        });
      });
      clearErrors === null || clearErrors === void 0 ? void 0 : clearErrors('queueLocations');
    } else {
      var converted = totalVolumesData.map(function (v, periodIdx) {
        var _a;
        return __assign(__assign({}, v), (_a = {}, _a[name] = convertField(v[name], periodIdx), _a));
      });
      setValue === null || setValue === void 0 ? void 0 : setValue('volumes', converted, {
        shouldDirty: true
      });
      clearErrors === null || clearErrors === void 0 ? void 0 : clearErrors('volumes');
    }
    setValue === null || setValue === void 0 ? void 0 : setValue(entryModeField, newMode, {
      shouldDirty: true
    });
  }, [entryModeField, entryMode, hasLocations, shouldConvert, queueLocationsData, totalVolumesData, name, decimal, setValue, clearErrors]);
  var handleEntryModeChange = useCallback(function (e) {
    var newMode = e.target.value;
    var oldMode = entryMode !== null && entryMode !== void 0 ? entryMode : LocationTableEntryMode.Percentage;
    if (newMode === oldMode) return;
    if (shouldConfirmAttritionModeChange && hasEnteredAttritionData) {
      setPendingEntryMode(newMode);
      return;
    }
    applyEntryModeChange(newMode);
  }, [applyEntryModeChange, entryMode, hasEnteredAttritionData, shouldConfirmAttritionModeChange]);
  var handleConfirmEntryModeChange = useCallback(function () {
    if (!pendingEntryMode) return;
    applyEntryModeChange(pendingEntryMode);
    setPendingEntryMode(undefined);
  }, [applyEntryModeChange, pendingEntryMode]);
  var handleCancelEntryModeChange = useCallback(function () {
    setPendingEntryMode(undefined);
  }, []);
  return {
    handleEntryModeChange: handleEntryModeChange,
    entryMode: entryMode,
    pendingEntryMode: pendingEntryMode,
    handleConfirmEntryModeChange: handleConfirmEntryModeChange,
    handleCancelEntryModeChange: handleCancelEntryModeChange
  };
};
export var useLocationSummary = function (_a) {
  var _b, _c;
  var name = _a.name,
    periodLength = _a.periodLength,
    chunkSize = _a.chunkSize,
    decimal = _a.decimal;
  var control = ((_b = useFormlessContext()) !== null && _b !== void 0 ? _b : {}).control;
  var queueLocationsRaw = useWatch({
    name: 'queueLocations',
    control: control
  });
  var queueLocationsData = useMemo(function () {
    return queueLocationsRaw !== null && queueLocationsRaw !== void 0 ? queueLocationsRaw : [];
  }, [queueLocationsRaw]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  var volumesForm = (_c = useWatch({
    name: 'volumes',
    control: control
  })) !== null && _c !== void 0 ? _c : [];
  var locations = useQueueLocations();
  /** Used when there are no queue locations - summarises the global volume row */
  var summaryGlobal = useMemo(function () {
    if (locations.length > 0) return [];
    var values = times(periodLength, function (i) {
      var _a, _b;
      return Number((_b = (_a = volumesForm[i]) === null || _a === void 0 ? void 0 : _a[name]) !== null && _b !== void 0 ? _b : 0);
    });
    return computeChunkSummary(values, chunkSize, decimal);
  }, [locations.length, periodLength, chunkSize, name, decimal, volumesForm]);
  /** Per-location summaries keyed by locIdx */
  var summary = useMemo(function () {
    return Object.fromEntries(locations.map(function (_loc, locIdx) {
      var values = times(periodLength, function (i) {
        var _a, _b, _c;
        return Number((_c = (_b = (_a = queueLocationsData[locIdx]) === null || _a === void 0 ? void 0 : _a.volumes) === null || _b === void 0 ? void 0 : _b[i]) === null || _c === void 0 ? void 0 : _c[name]);
      });
      return [locIdx, {
        chunks: computeChunkSummary(values, chunkSize, decimal)
      }];
    }));
  }, [locations, periodLength, chunkSize, name, decimal, queueLocationsData]);
  return {
    summaryGlobal: summaryGlobal,
    summary: summary,
    queueLocationsData: queueLocationsData
  };
};
export var useTypeSums = function (_a) {
  var _b;
  var name = _a.name,
    periodLength = _a.periodLength,
    decimal = _a.decimal,
    showTypeSums = _a.showTypeSums;
  var control = ((_b = useFormlessContext()) !== null && _b !== void 0 ? _b : {}).control;
  var queueLocationsRaw = useWatch({
    name: 'queueLocations',
    control: control
  });
  var queueLocationsData = useMemo(function () {
    return queueLocationsRaw !== null && queueLocationsRaw !== void 0 ? queueLocationsRaw : [];
  }, [queueLocationsRaw]);
  var groupedLocations = useGroupedLocations();
  var locIndexMap = useLocationIndexMap();
  var typeSums = useMemo(function () {
    if (!showTypeSums) return {};
    var result = {};
    for (var _i = 0, _a = Object.entries(groupedLocations); _i < _a.length; _i++) {
      var _b = _a[_i],
        locType = _b[0],
        countries = _b[1];
      var typeLocIndices = Object.values(countries).flat().map(function (loc) {
        return locIndexMap.get(loc.id);
      }).filter(function (idx) {
        return idx !== undefined;
      });
      result[locType] = computeTypeSums({
        locationTypeIndices: typeLocIndices,
        periodLength: periodLength,
        decimal: decimal,
        queueLocationsData: queueLocationsData,
        name: name
      });
    }
    return result;
  }, [showTypeSums, groupedLocations, locIndexMap, periodLength, name, decimal, queueLocationsData]);
  var grandTotals = useMemo(function () {
    if (!showTypeSums) return [];
    return times(periodLength, function (periodIdx) {
      var vals = Object.values(typeSums).map(function (arr) {
        return arr[periodIdx];
      });
      if (!vals.length || vals.every(function (v) {
        return v === null;
      })) return null;
      return round(sumBy(vals, function (v) {
        return v !== null && v !== void 0 ? v : 0;
      }), decimal !== null && decimal !== void 0 ? decimal : 0);
    });
  }, [showTypeSums, typeSums, periodLength, decimal]);
  return {
    typeSums: typeSums,
    grandTotals: grandTotals
  };
};